import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Users, Globe, Mail, ShieldAlert, ShieldCheck, AlertTriangle, RefreshCw, Activity, CheckCircle, Play } from 'lucide-react';
import { toast } from 'sonner';
import { YamlSourceBadge } from '@/components/YamlSourceBadge';

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState({});
  const [loadingScores, setLoadingScores] = useState({});
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notif, setNotif] = useState({ telegram_enabled: false, telegram_chat_id: '' });

  const fetchScore = async (domain) => {
    setLoadingScores(prev => ({ ...prev, [domain]: true }));
    try {
      const api = authAxios();
      const res = await api.get(`/domains/${domain}/score`);
      setScores(prev => ({ ...prev, [domain]: res.data }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingScores(prev => ({ ...prev, [domain]: false }));
    }
  };


  const fetchData = async () => {
    try {
      const api = authAxios();
      const [res, notifRes] = await Promise.all([
        api.get(`/customers/${customerId}/overview`),
        api.get(`/customers/${customerId}/notification-settings`)
      ]);
      setData(res.data);
      const n = notifRes.data || {};
      setNotif({
        telegram_enabled: !!n.telegram_enabled,
        telegram_chat_id: n.telegram_chat_id || ''
      });
    } catch (err) {
      toast.error('Failed to fetch customer data');
      navigate('/customers');
    } finally {
      setLoading(false);
      setNotifLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [customerId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { customer, stats, domains } = data;

  const saveNotificationSettings = async (next) => {
    setNotifSaving(true);
    try {
      const api = authAxios();
      const payload = {
        telegram_enabled: !!next.telegram_enabled,
        telegram_chat_id: next.telegram_chat_id ? String(next.telegram_chat_id).trim() : null
      };
      const res = await api.put(`/customers/${customerId}/notification-settings`, payload);
      const n = res.data || {};
      setNotif({
        telegram_enabled: !!n.telegram_enabled,
        telegram_chat_id: n.telegram_chat_id || ''
      });
      toast.success('Telegram-Einstellungen gespeichert');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Speichern fehlgeschlagen';
      toast.error(msg);
    } finally {
      setNotifSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/customers')}
          className="p-2 rounded-lg hover:bg-white/5 text-[#a1a1aa] hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold font-heading text-white">{customer.name}</h1>
            <YamlSourceBadge className="text-xs" />
          </div>
          <div className="flex items-center gap-2 mt-1 text-sm text-[#a1a1aa]">
            <Users className="w-4 h-4" />
            <span>{customer.id}</span>
            <Badge variant="outline" className="ml-2 border-white/10 text-[#a1a1aa]">
              {customer.type}
            </Badge>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Total Domains" value={domains.length} icon={Globe} color="text-blue-500" />
        <StatCard label="Healthy" value={stats.ok} icon={ShieldCheck} color="text-emerald-500" />
        <StatCard label="Issues" value={stats.dead + stats.miswired} icon={AlertTriangle} color="text-red-500" />
        <StatCard label="TLS Expiring" value={stats.tls_expiring_30} icon={ShieldAlert} color="text-amber-500" />
      </div>

      <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">Telegram Benachrichtigungen</h3>
          <p className="text-sm text-[#a1a1aa] mt-1">Steuert, ob dieser Kunde Fehler-Alerts per Telegram erhält.</p>
        </div>
        <CardContent className="p-6">
          {notifLoading ? (
            <div className="flex items-center gap-2 text-sm text-[#a1a1aa]">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Lädt…
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="telegram-enabled"
                  checked={!!notif.telegram_enabled}
                  onCheckedChange={(v) => setNotif((prev) => ({ ...prev, telegram_enabled: !!v }))}
                />
                <label htmlFor="telegram-enabled" className="text-sm text-white">Telegram Notifications aktiv</label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div className="md:col-span-2">
                  <label className="block text-xs text-[#a1a1aa] mb-2">Telegram Chat-ID</label>
                  <Input
                    value={notif.telegram_chat_id}
                    onChange={(e) => setNotif((prev) => ({ ...prev, telegram_chat_id: e.target.value }))}
                    placeholder="z.B. -1001234567890"
                    className="h-11 bg-[#0c0c0e] border-white/10 focus:border-[#EF694C] focus:ring-1 focus:ring-[#EF694C] text-white placeholder:text-[#52525b] rounded-lg"
                  />
                  <div className="mt-2 text-xs text-[#a1a1aa]">Pflicht, wenn Telegram Notifications aktiv sind.</div>
                </div>
                <Button
                  onClick={() => saveNotificationSettings(notif)}
                  disabled={notifSaving}
                  className="h-11 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg"
                >
                  {notifSaving ? 'Speichern…' : 'Speichern'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Domains Table */}
      <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">Managed Domains</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#a1a1aa]">Domain</TableHead>
                <TableHead className="text-[#a1a1aa]">Status</TableHead>
                <TableHead className="text-[#a1a1aa]">Asset</TableHead>
                <TableHead className="text-[#a1a1aa]">TLS</TableHead>
                <TableHead className="text-[#a1a1aa]">Mail</TableHead>
                <TableHead className="text-[#a1a1aa]">Fyre-Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {domains.map((dom) => (
                <TableRow key={dom.domain} className="border-white/5 hover:bg-white/[0.03]">
                  <TableCell className="font-medium text-white font-mono">
                    {dom.domain}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={dom.latest_status} />
                  </TableCell>
                  <TableCell className="text-[#a1a1aa] text-sm">
                    {dom.mapped_asset_id || '-'}
                  </TableCell>
                  <TableCell>
                    {dom.tls_days !== null ? (
                      <div className={`flex items-center gap-1 text-xs ${
                        dom.tls_days <= 30 ? 'text-amber-500' : 'text-emerald-500'
                      }`}>
                        {dom.tls_days <= 30 ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                        {dom.tls_days}d
                      </div>
                    ) : (
                      <span className="text-[#52525b] text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {dom.mail?.mail_domain && (
                      <div className="flex items-center gap-1 text-[#a1a1aa] text-xs">
                        <Mail className="w-3 h-3" />
                        {dom.mail.mail_domain}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {scores[dom.domain] ? (
                      <div className="flex items-center gap-2">
                        <span className={`font-bold px-2 py-1 rounded-md text-xs ${
                          scores[dom.domain].score >= 90 ? "bg-green-500/10 text-green-500" : 
                          scores[dom.domain].score >= 70 ? "bg-yellow-500/10 text-yellow-500" : 
                          "bg-red-500/10 text-red-500"
                        }`}>
                          {scores[dom.domain].score} / 100 ({scores[dom.domain].grade})
                        </span>
                      </div>
                    ) : (
                      <button 
                        onClick={() => fetchScore(dom.domain)}
                        disabled={loadingScores[dom.domain]}
                        className="flex items-center gap-1 text-xs bg-white/5 hover:bg-white/10 text-white px-2 py-1 rounded disabled:opacity-50 transition-colors"
                      >
                        {loadingScores[dom.domain] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                        Score prüfen
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card className="bg-[#121214] border-white/10 rounded-xl">
    <CardContent className="p-6 flex items-center justify-between">
      <div>
        <p className="text-xs text-[#a1a1aa] uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-white mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-lg bg-white/5 ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => {
  const styles = {
    ok: 'bg-emerald-500/10 text-emerald-500',
    dead: 'bg-red-500/10 text-red-500',
    miswired: 'bg-amber-500/10 text-amber-500',
    external: 'bg-blue-500/10 text-blue-500',
    unknown: 'bg-gray-500/10 text-gray-500'
  };
  return (
    <Badge className={`${styles[status] || styles.unknown} border-0 capitalize`}>
      {status}
    </Badge>
  );
};
